import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {
  MEMBERS_APP_ID,
  SANTA_MEMBERS_APP_ID,
} from '@wix/communities-universal/dist/src/constants/appsConfig';
import { permissionsChecker } from './permissions-checker';
import { getCurrentUser } from '../../store/current-user/current-user-selectors';
import { getApplicationConfig } from '../../selectors/communities-context-selectors';
import { getCommunityId } from '../../selectors/forum-data-selectors';
import PermissionsContext from './permissions-context';

/*
Which permissions checker should I use?

* can - use when you only care about if user can do action or not - using this carries a message
  that action is visible only when user can actually do it.
* canSee - use it when you want to show that user could do an action, even
  if he currently can't. For example - like a post as logged in, but private member.
  Using this should be accompanied by decorating for user to login or become a public member.
* canRender - use it everywhere where you render UI based on permissions. It checks
  both can and canSee.*/

const createPermissionsChecker = (permissions, isSee) => (permission, resource, obj) => {
  return isSee
    ? permissions.canSee(permissions.resourcify(obj, resource), permission)
    : permissions.can(permissions.resourcify(obj, resource), permission);
};

const createPermissionsRenderHelper = (canSeeChecker, canChecker) => (
  permission,
  resource,
  obj,
  render,
) => {
  const renderFunc = render ? render : obj;
  const can = canChecker(permission, resource, obj);
  const canSee = canSeeChecker(permission, resource, obj);
  return can || canSee ? renderFunc(can, canSee) : null;
};

class PermissionsContextProvider extends Component {
  constructor(props) {
    super(props);

    const state = props.state;

    const isOldForum = Boolean(getCommunityId(state));
    const isMemberAppInstalled = get(
      getApplicationConfig(state, MEMBERS_APP_ID),
      'isInstalled',
      false,
    );
    const isMemberAreaInstalled = get(
      getApplicationConfig(state, SANTA_MEMBERS_APP_ID),
      'isInstalled',
      false,
    );
    const flags = { isOldForum, isMemberAppInstalled, isMemberAreaInstalled };
    this.permissions = permissionsChecker(flags, getCurrentUser(state), resource =>
      this.props.resourceResolver(this.props.state)(resource),
    );
    this.createHelpers();
  }

  updateUser = user => {
    if (this.lastUser === undefined) {
      this.lastUser = user;
    }

    if (user && this.lastUser !== user) {
      this.lastUser = user;
      this.permissions.setUserIdentity(user);
    }
  };

  createHelpers = () => {
    const canSeePermissionsHelper = createPermissionsChecker(this.permissions, true);
    const canPermissionsHelper = createPermissionsChecker(this.permissions, false);
    const permissionsRenderHelper = createPermissionsRenderHelper(
      canSeePermissionsHelper,
      canPermissionsHelper,
    );

    this.helpers = {
      permissions: this.permissions,
      can: canPermissionsHelper,
      canSee: canSeePermissionsHelper,
      canRender: permissionsRenderHelper,
    };
  };

  render() {
    this.updateUser(getCurrentUser(this.props.state));

    return (
      <PermissionsContext.Provider value={this.helpers}>
        {this.props.children}
      </PermissionsContext.Provider>
    );
  }
}

PermissionsContextProvider.propTypes = {
  state: PropTypes.object.isRequired,
  children: PropTypes.element.isRequired,
  resourceResolver: PropTypes.func.isRequired,
};

export default PermissionsContextProvider;
